/**
 * useDrugsLibrary — 2026-06-08 Phase 4E wire-up for the canonical
 * Q Manual VG35002 drugs + poisons catalog.
 *
 * Mirrors useGadgetsLibrary.ts pattern — single manifest fetch + active
 * tab state + per-entry detail fetch. Tab set is the 2 seed categories
 * directly (drug + poison), no super-category clustering.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DrugManifestEntry } from '../services/host/DrugsServiceModule';
import { DrugsServiceModule } from '../services/host/DrugsServiceModule';

export type DrugsLibraryState = 'idle' | 'loading' | 'ready' | 'error';

export type DrugsLibraryTab = 'drug' | 'poison';

export interface DrugsLibraryHookResult {
  state: DrugsLibraryState;
  drugs: DrugManifestEntry[];
  error: string | null;
  refresh: () => void;
  activeTab: DrugsLibraryTab;
  setActiveTab: (tab: DrugsLibraryTab) => void;
  selectedDrugId: string | null;
  selectedDrugData: Record<string, unknown> | null;
  selectedDrugState: DrugsLibraryState;
  selectDrug: (drugId: string | null) => void;
}

export function useDrugsLibrary(
  drugsService?: DrugsServiceModule,
): DrugsLibraryHookResult {
  const resolvedService = useMemo(
    () => drugsService ?? new DrugsServiceModule(),
    [drugsService],
  );

  const [state, setState] = useState<DrugsLibraryState>('idle');
  const [drugs, setDrugs] = useState<DrugManifestEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] = useState<DrugsLibraryTab>('drug');

  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [selectedDrugData, setSelectedDrugData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedDrugState, setSelectedDrugState] =
    useState<DrugsLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectDrug = useCallback((drugId: string | null) => {
    setSelectedDrugId(drugId);
    if (drugId === null) {
      setSelectedDrugData(null);
      setSelectedDrugState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setDrugs(result.drugs);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setDrugs([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedDrugId === null) {
      return;
    }
    let cancelled = false;

    setSelectedDrugState('loading');
    setSelectedDrugData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedDrugId);
        if (cancelled) return;
        if (result === null) {
          setSelectedDrugState('error');
          return;
        }
        setSelectedDrugData(result);
        setSelectedDrugState('ready');
      } catch {
        if (cancelled) return;
        setSelectedDrugState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedDrugId]);

  return {
    state,
    drugs,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedDrugId,
    selectedDrugData,
    selectedDrugState,
    selectDrug,
  };
}
